import { useState, useEffect, useCallback, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  MessageCircle,
  Users,
  Send,
  List,
  Calendar,
  Shield,
  LogOut,
  Activity,
  ImageIcon,
  MessageCircleReply,
  DollarSign,
  FileText,
  Lock,
  Settings,
  Youtube,
  MessageSquare,
  Instagram,
  BarChart2,
  BarChart3,
  HeartPulse,
  Puzzle,
  Download,
  Link2,
  Trash2,
  ShoppingCart,
  Radar,
  TrendingUp,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  ChevronRight,
  Bot,
  Globe,
  Megaphone,
  FolderOpen,
  Wrench,
  Workflow,
  Sparkles,
  Bell,
  Search,
  ArrowRightLeft,
  StickyNote,
  Mail,
} from 'lucide-react'
import { useAuth, useLogout } from '@/api/hooks/useAuth'
import { useLanguage } from '@/i18n/LanguageContext'
import { useUIStore } from '@/stores/uiStore'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useTokenWarnings } from '@/components/shared/ScheduleAlert'
import { cn } from '@/lib/utils'
import { isStagingMode } from '@/lib/staging'

/* ------------------------------------------------------------------ */
/*  Nav structure — grouped by platform/function                       */
/* ------------------------------------------------------------------ */

interface NavItem {
  to: string
  label: string
  icon: React.ElementType
}

interface NavSection {
  id: string
  label: string
  icon: React.ElementType
  items: NavItem[]
  adminOnly?: boolean
}

const navSections: NavSection[] = [
  {
    id: 'principal',
    label: 'Principal',
    icon: LayoutDashboard,
    items: [
      { to: '/painel', label: 'Painel', icon: LayoutDashboard },
      { to: '/chat', label: 'Chat', icon: MessageCircle },
      { to: '/contatos', label: 'Leads', icon: Users },
    ],
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    items: [
      { to: '/instagram', label: 'Revisão', icon: Instagram },
      { to: '/ig-crescimento', label: 'Crescimento', icon: TrendingUp },
      { to: '/recuperar-comentarios', label: 'Recuperar Comentários', icon: MessageCircleReply },
    ],
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: Globe,
    items: [
      { to: '/paginas', label: 'Páginas', icon: FileText },
      { to: '/gerenciador', label: 'Gerenciador', icon: Shield },
      { to: '/extensao', label: 'Extensão', icon: Puzzle },
      { to: '/engagement', label: 'Engagement', icon: BarChart3 },
      { to: '/automacao-fb', label: 'Automacao FB', icon: Workflow },
      { to: '/transfer', label: 'Transfer Manager', icon: ArrowRightLeft },
    ],
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: MessageSquare,
    items: [
      { to: '/whatsapp', label: 'Instâncias', icon: MessageSquare },
    ],
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: Send,
    items: [
      { to: '/telegram', label: 'Telegram', icon: Send },
    ],
  },
  {
    id: 'youtube',
    label: 'YouTube',
    icon: Youtube,
    items: [
      { to: '/youtube', label: 'Canais', icon: Youtube },
    ],
  },
  {
    id: 'ad-manager',
    label: 'Ad Manager',
    icon: BarChart2,
    items: [
      { to: '/ad-manager', label: 'Ad Manager', icon: BarChart2 },
      { to: '/adskeeper', label: 'Adskeeper', icon: BarChart2 },
    ],
  },
  {
    id: 'automacao',
    label: 'Automação',
    icon: Bot,
    items: [
      { to: '/sequencias', label: 'Sequências', icon: List },
      { to: '/broadcast', label: 'Broadcast', icon: Send },
      { to: '/card-de-captura', label: 'Card de Captura', icon: Sparkles },
      { to: '/push', label: 'Push', icon: Bell },
      { to: '/emails', label: 'Emails', icon: Mail },
    ],
  },
  {
    id: 'conteudo',
    label: 'Conteúdo',
    icon: FolderOpen,
    items: [
      { to: '/publicar', label: 'Publicar', icon: Calendar },
      { to: '/midias', label: 'Mídias', icon: ImageIcon },
      { to: '/download-midias', label: 'Download Mídias', icon: Download },
      { to: '/encurtador', label: 'Encurtador', icon: Link2 },
      { to: '/exclusao', label: 'Exclusão', icon: Trash2 },
    ],
  },
  {
    id: 'gestao',
    label: 'Gestão',
    icon: Wrench,
    items: [
      { to: '/financeiro', label: 'Financeiro', icon: DollarSign },
      { to: '/rastreamento', label: 'Rastreamento', icon: Radar },
      { to: '/creator', label: 'Creator', icon: Bot },
      { to: '/notas', label: 'Notas', icon: StickyNote },
      { to: '/status', label: 'Status', icon: HeartPulse },
      { to: '/configuracoes', label: 'Configurações', icon: Settings },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    icon: Shield,
    adminOnly: true,
    items: [
      { to: '/vendas', label: 'Vendas', icon: ShoppingCart },
      { to: '/admin', label: 'Admin', icon: Shield },
      { to: '/monitor', label: 'Monitor', icon: Activity },
    ],
  },
]

const stagingNavSections: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    items: [
      { to: '/painel', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    id: 'facebook',
    label: 'Facebook Pages',
    icon: Globe,
    items: [
      { to: '/paginas', label: 'Pages', icon: FileText },
      { to: '/gerenciador', label: 'Page Manager', icon: Shield },
      { to: '/engagement', label: 'Engagement', icon: BarChart3 },
    ],
  },
  {
    id: 'messaging',
    label: 'Messaging',
    icon: Send,
    items: [
      { to: '/broadcast', label: 'Broadcast', icon: Send },
      { to: '/chat', label: 'Chat', icon: MessageCircle },
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  SidebarLink                                                        */
/* ------------------------------------------------------------------ */
function SidebarLink({
  to,
  label,
  icon: Icon,
  locked,
  badge,
  expanded,
}: {
  to: string
  label: string
  icon: React.ElementType
  locked?: boolean
  badge?: number
  expanded: boolean
}) {
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)

  if (locked) {
    return null
  }

  return (
    <NavLink
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center rounded-lg text-sm font-medium transition-all duration-150',
          expanded ? 'gap-2.5 pl-9 pr-3 py-1.5' : 'justify-center h-[34px] w-[34px] mx-auto',
          isActive
            ? 'bg-sidebar-accent text-primary'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        )
      }
      title={!expanded ? label : undefined}
    >
      {({ isActive }) => (
        <>
          {isActive && expanded && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-primary" />
          )}
          {!expanded && <Icon className="h-4 w-4 shrink-0" />}
          {expanded && <span className="truncate text-[11px]">{label}</span>}
          {expanded && badge && badge > 0 ? (
            <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
              {badge > 99 ? '99+' : badge}
            </span>
          ) : null}
          {!expanded && badge && badge > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-destructive px-0.5 text-[7px] font-bold text-destructive-foreground">
              {badge > 9 ? '9+' : badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}

/* ------------------------------------------------------------------ */
/*  CollapsibleSection                                                  */
/* ------------------------------------------------------------------ */
function CollapsibleSection({
  section,
  expanded,
  isOpen,
  onToggle,
  badgeMap,
  isLocked,
  t,
}: {
  section: NavSection
  expanded: boolean
  isOpen: boolean
  onToggle: () => void
  badgeMap: Record<string, number | undefined>
  isLocked: (path: string) => boolean
  t: (s: string) => string
}) {
  const location = useLocation()
  const SectionIcon = section.icon

  // Check if any child route is active
  const hasActiveChild = section.items.some(item => location.pathname === item.to)

  // Total badge count for the section
  const totalBadge = section.items.reduce((sum, item) => sum + (badgeMap[item.to] || 0), 0)

  if (!expanded) {
    // Collapsed: show section icon as a button, tooltip shows section label
    return (
      <div className="relative">
        <button
          onClick={onToggle}
          className={cn(
            'relative flex h-[38px] w-[38px] mx-auto items-center justify-center rounded-[10px] transition-all duration-150',
            hasActiveChild
              ? 'bg-sidebar-accent text-primary'
              : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
          )}
          title={t(section.label)}
        >
          {hasActiveChild && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[13px] w-[3px] h-5 bg-primary rounded-r-sm" />
          )}
          <SectionIcon className="h-4 w-4" />
          {totalBadge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-destructive px-0.5 text-[7px] font-bold text-destructive-foreground">
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </button>
      </div>
    )
  }

  // Expanded: collapsible section with header + children
  return (
    <div>
      <button
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[11px] font-semibold transition-all duration-150',
          hasActiveChild
            ? 'text-primary'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <SectionIcon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left truncate">{t(section.label)}</span>
        {totalBadge > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
        <ChevronRight
          className={cn(
            'h-3 w-3 shrink-0 text-muted-foreground/50 transition-transform duration-200',
            isOpen && 'rotate-90'
          )}
        />
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5">
          {section.items.map((item) => (
            <SidebarLink
              key={item.to}
              {...item}
              label={t(item.label)}
              locked={isLocked(item.to)}
              badge={badgeMap[item.to]}
              expanded={expanded}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sidebar (main export)                                              */
/* ------------------------------------------------------------------ */
export function Sidebar({ mobile }: { mobile?: boolean }) {
  const { t, language } = useLanguage()
  const { data: user } = useAuth()
  const logout = useLogout()
  const location = useLocation()
  const initial = (user?.name || 'U').charAt(0).toUpperCase()

  const pinned = useSidebarStore((s) => s.pinned)
  const togglePinned = useSidebarStore((s) => s.togglePinned)
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)

  const [hovered, setHovered] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Clear sidebar search whenever the user navigates so it doesn't persist
  // between visits to different pages.
  useEffect(() => {
    setSearchQuery('')
  }, [location.pathname])

  // Track which sections are open — ALL open by default
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const sections = isStagingMode() ? stagingNavSections : navSections
    return new Set(sections.map(s => s.id))
  })

  const toggleSection = useCallback((id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const expanded = mobile || pinned || hovered

  // Hide certain items during EN mode (Meta review) or per user
  const enHidePaths: string[] = []
  const userHidePaths: string[] = []
  const hidePaths = language === 'en' ? enHidePaths : userHidePaths
  const filterItems = (items: NavItem[]) =>
    hidePaths.length > 0 ? items.filter((i) => !hidePaths.includes(i.to)) : items

  const tokenWarnings = useTokenWarnings()
  const expiredTokenCount = tokenWarnings.filter((w) => w.type === 'expired').length

  const allowedPages = user?.allowedPages
  const isLocked = (path: string) => (allowedPages ? !allowedPages.includes(path) : false)

  // Badge map for routes that show notification counts
  const badgeMap: Record<string, number | undefined> = {
    '/painel': expiredTokenCount,
    '/publicar': expiredTokenCount,
  }

  // Filter sections based on admin status and visible items
  const activeSections = isStagingMode() ? stagingNavSections : navSections
  const visibleSections = activeSections
    .filter(s => !s.adminOnly || user?.isAdmin)
    .map(s => ({ ...s, items: filterItems(s.items) }))
    .filter(s => s.items.length > 0)

  // Apply search filter — match by translated label (item or section)
  const normalizedQuery = searchQuery.trim().toLowerCase()
  const filteredSections = useMemo(() => {
    if (!normalizedQuery) return visibleSections
    return visibleSections
      .map(section => {
        const sectionMatches = t(section.label).toLowerCase().includes(normalizedQuery)
        const matchingItems = section.items.filter(item =>
          t(item.label).toLowerCase().includes(normalizedQuery)
        )
        // If section name itself matches, keep all items so user can browse the section
        return { ...section, items: sectionMatches ? section.items : matchingItems }
      })
      .filter(s => s.items.length > 0)
  }, [normalizedQuery, visibleSections, t])

  // While searching, auto-expand any section with results
  const effectiveOpenSections = useMemo(() => {
    if (!normalizedQuery) return openSections
    return new Set(filteredSections.map(s => s.id))
  }, [normalizedQuery, filteredSections, openSections])

  const handleMouseEnter = useCallback(() => {
    if (!mobile) setHovered(true)
  }, [mobile])

  const handleMouseLeave = useCallback(() => {
    if (!mobile) setHovered(false)
  }, [mobile])

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'flex h-full flex-col bg-sidebar transition-all duration-200 overflow-hidden',
        mobile ? 'w-[220px]' : expanded ? 'w-[220px]' : 'w-16'
      )}
    >
      {/* ---- Brand ---- */}
      <div className={cn(
        'flex items-center shrink-0 transition-all duration-200',
        expanded ? 'gap-3 px-4 py-4' : 'justify-center py-4'
      )}>
        <img
          src="/images/logo.png"
          alt="Blissf"
          className="h-9 w-9 shrink-0 rounded-[10px] object-cover"
        />
        {expanded && (
          <div className="flex flex-1 items-center justify-between min-w-0">
            <span className="text-[15px] font-bold tracking-tight text-foreground truncate">Blissf</span>
            {!mobile && (
              <button
                onClick={togglePinned}
                className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"
                title={pinned ? 'Recolher sidebar' : 'Fixar sidebar'}
              >
                {pinned ? (
                  <PanelLeftClose className="h-3.5 w-3.5" />
                ) : (
                  <PanelLeftOpen className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {mobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* ---- Search (only when expanded) ---- */}
      {expanded && (
        <div className="shrink-0 px-3 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('Buscar no menu...')}
              className="h-7 w-full rounded-lg border border-border/40 bg-secondary/40 pl-7 pr-7 text-[11px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary/40 focus:bg-background focus:outline-none transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors"
                title={t('Limpar')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ---- Navigation ---- */}
      <nav className={cn(
        'flex-1 overflow-y-auto overflow-x-hidden py-2 transition-all duration-200',
        expanded ? 'px-2 space-y-1' : 'px-[13px] space-y-1'
      )}>
        {filteredSections.length === 0 ? (
          expanded && (
            <div className="px-3 py-6 text-center">
              <p className="text-[11px] text-muted-foreground">{t('Nenhum resultado')}</p>
            </div>
          )
        ) : (
          filteredSections.map((section) => (
            <CollapsibleSection
              key={section.id}
              section={section}
              expanded={expanded}
              isOpen={effectiveOpenSections.has(section.id)}
              onToggle={() => toggleSection(section.id)}
              badgeMap={badgeMap}
              isLocked={isLocked}
              t={t}
            />
          ))
        )}
      </nav>

      {/* ---- User section ---- */}
      <div className={cn(
        'shrink-0 border-t border-sidebar-border transition-all duration-200',
        expanded ? 'p-3' : 'p-2'
      )}>
        {expanded ? (
          <div className="flex items-center gap-3 rounded-[10px] bg-secondary px-3 py-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-sidebar-accent text-sm font-bold text-primary">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] font-semibold text-foreground">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground">
                {user?.isAdmin ? t('Administrador') : t('Cliente')}
              </div>
            </div>
            <button
              onClick={() => logout.mutate()}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive shrink-0"
              title={t('Sair')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-sidebar-accent text-sm font-bold text-primary cursor-default"
              title={user?.name}
            >
              {initial}
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}
